/**
 * Placed `.TBL` sphere nodes (§4.3 type 1) as instanced world geometry —
 * the tree canopies, which are the only sphere the shipped `.WLD` regions
 * actually place.
 *
 * **What the census says.** Over all six baked regions (1 892 placements,
 * 237 distinct object refs) exactly **one ref per region** carries sphere
 * nodes: the tree — `TERR5-83` in Verdun (37 copies), `TERR3-86` in the Somme
 * (40), `TERR4-124` in Paris (36), `TERR1-75` in London (19), `TERR2-86` in
 * Dunkirk (34) and `PTPLANE-12` (6). Each is three canopies of radius 4.68 m
 * sitting on six triangles of trunk, in palette colours 133/134 (olive), all
 * on LOD 1 at `detail` 255, all at `stateIndex` 0. So the whole of "trees have
 * no canopies over Verdun" is 111 spheres in two colours, and grouping the
 * region's spheres **by colour** rather than by object ref turns that into two
 * draw calls for the entire region instead of one per placed object.
 *
 * **Why the colour goes in the geometry and not in `instanceColor`.** Grouping
 * by colour is already forced by the batching decision above, and once a group
 * is one colour by construction, baking it into a per-group unit-sphere
 * geometry's vertex colours lets every sphere mesh share the *same*
 * `vertexColors` material as the region's triangle meshes — one material for
 * the whole scenery group, which is what `dispose-tree.ts` is written to
 * de-duplicate. It degrades gracefully too: an object with fifty sphere
 * colours would cost fifty draw calls rather than being wrong.
 *
 * An unlit sphere reads as a flat disc of solid colour from any angle, which
 * is precisely what the original draws — its type-1 handler calls a **circle
 * rasteriser** with a screen radius of `radius / depth` (§4.3). The
 * tessellation therefore only has to be round enough in silhouette.
 */

import * as THREE from "three/webgpu";
import type { BakedModelSphere } from "./baked-model.ts";
import { modelFrameToWorld } from "../world/placement-transform.ts";

/** Round enough in silhouette for a flat-shaded disc; 168 triangles against `/assets`' 384. */
const SPHERE_SEGMENTS = 12;
const SPHERE_RINGS = 8;
const RGB_MAX = 255;
/** A radius of exactly 0 makes a degenerate geometry; the corpus has none, but a bad bake should not throw. */
const MIN_RADIUS_M = 1e-4;

/** Where one copy of an object sits — the same yaw-then-translate `polygon-outlines.ts` applies to its line segments. */
export interface SpherePlacement {
  readonly yawRadians: number;
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/** One sphere in world metres, with its colour resolved to three.js's 0..1 convention. */
export interface WorldSphere {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly radius: number;
  readonly r: number;
  readonly g: number;
  readonly b: number;
}

/**
 * The state a placed object draws when nothing is animating it: §4.5's group
 * walk draws `stateArray[stateChannel]`, and a static instance has no state
 * array, so node 0.
 */
export const STATIC_STATE_INDEX = 0;

/**
 * Every copy of one object's spheres, in world metres. Model space goes
 * through the *same* `(x, y, z) -> (x, z, -y)` fix the triangle geometry gets
 * from `geometry.rotateX(-PI/2)` and the line segments get by hand, then the
 * placement's yaw about world Y, then its translation — a sphere that took a
 * different route would sit off its own trunk.
 */
export function worldSpheres(
  spheres: readonly BakedModelSphere[],
  placements: readonly SpherePlacement[],
): WorldSphere[] {
  const drawn = spheres.filter((sphere) => sphere.stateIndex === STATIC_STATE_INDEX);
  const out: WorldSphere[] = [];
  for (const placement of placements) {
    const cos = Math.cos(placement.yawRadians);
    const sin = Math.sin(placement.yawRadians);
    for (const sphere of drawn) {
      const local = modelFrameToWorld(sphere.position[0], sphere.position[1], sphere.position[2]);
      const [red, green, blue] = sphere.colorRgba;
      out.push({
        x: cos * local.x + sin * local.z + placement.x,
        y: local.y + placement.y,
        z: -sin * local.x + cos * local.z + placement.z,
        radius: Math.max(sphere.radius, MIN_RADIUS_M),
        r: (red ?? 0) / RGB_MAX,
        g: (green ?? 0) / RGB_MAX,
        b: (blue ?? 0) / RGB_MAX,
      });
    }
  }
  return out;
}

/** Buckets spheres by colour, keyed by the 8-bit triple so two olives one index apart stay apart. */
export function groupSpheresByColour(spheres: readonly WorldSphere[]): Map<string, WorldSphere[]> {
  const groups = new Map<string, WorldSphere[]>();
  for (const sphere of spheres) {
    const key = `${Math.round(sphere.r * RGB_MAX)},${Math.round(sphere.g * RGB_MAX)},${Math.round(sphere.b * RGB_MAX)}`;
    const bucket = groups.get(key);
    if (bucket) bucket.push(sphere);
    else groups.set(key, [sphere]);
  }
  return groups;
}

function colouredUnitSphere(r: number, g: number, b: number): THREE.BufferGeometry {
  const geometry = new THREE.SphereGeometry(1, SPHERE_SEGMENTS, SPHERE_RINGS);
  const vertexCount = geometry.getAttribute("position").count;
  const colors = new Float32Array(vertexCount * 3);
  for (let vertex = 0; vertex < vertexCount; vertex++) {
    colors[vertex * 3] = r;
    colors[vertex * 3 + 1] = g;
    colors[vertex * 3 + 2] = b;
  }
  geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  return geometry;
}

/**
 * One `InstancedMesh` per distinct sphere colour, all sharing `material` (the
 * region's one `vertexColors` material). Radius rides in the instance matrix's
 * scale, so every group shares the same unit-sphere tessellation.
 */
export function buildSphereMeshes(spheres: readonly WorldSphere[], material: THREE.Material): THREE.InstancedMesh[] {
  const matrix = new THREE.Matrix4();
  const meshes: THREE.InstancedMesh[] = [];
  for (const bucket of groupSpheresByColour(spheres).values()) {
    const first = bucket[0];
    if (!first) continue;
    const mesh = new THREE.InstancedMesh(colouredUnitSphere(first.r, first.g, first.b), material, bucket.length);
    bucket.forEach((sphere, index) => {
      matrix.makeScale(sphere.radius, sphere.radius, sphere.radius);
      matrix.setPosition(sphere.x, sphere.y, sphere.z);
      mesh.setMatrixAt(index, matrix);
    });
    mesh.instanceMatrix.needsUpdate = true;
    mesh.computeBoundingSphere();
    meshes.push(mesh);
  }
  return meshes;
}
